import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { X, KeyRound, Loader2, Eye, EyeOff } from "lucide-react";

export default function ChangePasswordModal({ open, onClose }) {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState({ current: false, next: false, confirm: false });

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (form.next !== form.confirm) {
      toast.error("New password and confirm password do not match");
      return;
    }
    if (form.next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current,
        new_password: form.next,
      });
      toast.success("Password updated. Use the new one from now on.");
      setForm({ current: "", next: "", confirm: "" });
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to change password");
    } finally {
      setBusy(false);
    }
  };

  const Field = ({ label, k, autoComplete }) => (
    <div>
      <label className="label-eyebrow block mb-1.5">{label}</label>
      <div className="relative">
        <input
          type={show[k] ? "text" : "password"}
          autoComplete={autoComplete}
          value={form[k]}
          onChange={(e) => setForm({ ...form, [k]: e.target.value })}
          className="ehi-input w-full pr-10"
          data-testid={`cp-${k}`}
          required
        />
        <button
          type="button"
          onClick={() => setShow((s) => ({ ...s, [k]: !s[k] }))}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
          tabIndex={-1}
          aria-label={show[k] ? "Hide password" : "Show password"}
        >
          {show[k] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4" data-testid="change-password-modal">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 relative">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 p-1 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100"
          aria-label="Close"
          data-testid="cp-close"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 mb-1">
          <div className="w-8 h-8 rounded-md bg-ehi-primary/10 text-ehi-primary grid place-items-center">
            <KeyRound className="w-4 h-4" />
          </div>
          <h3 className="font-display font-black text-xl text-slate-900">Change Password</h3>
        </div>
        <p className="text-xs text-slate-500 mb-5">Your new password is used on next login. Other sessions will need to sign in again.</p>

        <div className="space-y-4">
          <Field label="Current password" k="current" autoComplete="current-password" />
          <Field label="New password (min 8 chars)" k="next" autoComplete="new-password" />
          <Field label="Confirm new password" k="confirm" autoComplete="new-password" />
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-sm px-4 py-2 rounded border border-slate-200" data-testid="cp-cancel">Cancel</button>
          <button
            type="submit"
            disabled={busy}
            className="btn-accent inline-flex items-center gap-2 text-sm px-4 py-2 disabled:opacity-60"
            data-testid="cp-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            {busy ? "Updating…" : "Update password"}
          </button>
        </div>
      </form>
    </div>
  );
}
